import { useNavigate, useParams } from "react-router-dom";
import { formatCurrency } from "../../lib/utils";
import { useGetDispatchByIdQuery } from "./dispatchApiSlice";

// ─── Skeleton ─────────────────────────────────────────────────────────────────
const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-100 rounded ${className}`} />
);

const SkeletonCard = () => (
  <div className="bg-white p-5 flex flex-col gap-2">
    <Skeleton className="h-2.5 w-3/5" />
    <Skeleton className="h-4 w-4/5" />
  </div>
);

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (d: string) =>
  d
    ? new Date(d).toLocaleString("en-US", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const pct = (n: number) => `${Number(n ?? 0).toFixed(1)}%`;

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({
  status,
  completed,
}: {
  status: string;
  completed: boolean;
}) => (
  <span
    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium tracking-wide ${
      completed
        ? "bg-emerald-100 text-emerald-700"
        : "bg-amber-100 text-amber-700"
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        completed ? "bg-emerald-500" : "bg-amber-500"
      }`}
    />
    {status || (completed ? "Completed" : "In Progress")}
  </span>
);

const InfoCard = ({
  label,
  value,
  accent = false,
}: {
  label: string;
  value: string | number;
  accent?: boolean;
}) => (
  <div
    className={`flex flex-col gap-1.5 p-5 transition-colors ${
      accent ? "bg-amber-50 hover:bg-amber-100/60" : "bg-white hover:bg-gray-50"
    }`}
  >
    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-medium">
      {label}
    </span>
    <span className="font-semibold text-gray-900 text-sm leading-snug break-words">
      {value}
    </span>
  </div>
);

const Section = ({
  title,
  icon,
  children,
}: {
  title: string;
  icon: string;
  children: React.ReactNode;
}) => (
  <section className="mt-8">
    <h2 className="flex items-center gap-2 text-[11px] uppercase tracking-widest font-bold text-gray-400 mb-3">
      <span className="text-base">{icon}</span>
      {title}
    </h2>
    {children}
  </section>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const DispatchDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useGetDispatchByIdQuery(id || "", {
    skip: !id,
  });
  const navigate = useNavigate();

  // ── Loading State ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="bg-gray-900 px-6 sm:px-10 py-9 flex items-start justify-between flex-wrap gap-4">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-2.5 w-28 bg-gray-700" />
            <Skeleton className="h-7 w-48 bg-gray-700" />
            <Skeleton className="h-3.5 w-36 bg-gray-700" />
          </div>
          <Skeleton className="h-7 w-28 rounded-full bg-gray-700" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-b border-gray-100">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="p-6 border-r border-gray-100 flex flex-col gap-2"
            >
              <Skeleton className="h-2.5 w-3/5" />
              <Skeleton className="h-6 w-4/5" />
            </div>
          ))}
        </div>
        <div className="px-6 sm:px-10 pb-16">
          {[8, 4, 3].map((cols, s) => (
            <div key={s} className="mt-8">
              <Skeleton className="h-3 w-32 mb-4" />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden">
                {Array.from({ length: cols }).map((_, i) => (
                  <SkeletonCard key={i} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error State ────────────────────────────────────────────────────────────
  if (isError) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3 text-gray-400">
        <span className="text-5xl">⚠️</span>
        <h2 className="text-xl font-bold text-gray-800">
          Failed to load dispatch
        </h2>
        <p className="text-sm">
          Unable to retrieve dispatch details. Please try again.
        </p>
      </div>
    );
  }

  if (!data) return null;

  const dispatch = data.data;

  // ── Data State ─────────────────────────────────────────────────────────────
  return (
    <div className="mbg-white text-gray-900">
      {/* ── Header ── */}
      <div className="bg-gradient-to-br from-gray-50 to-gray-100 px-6 sm:px-10 py-10">
        <div className="flex flex-col gap-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <button
              onClick={() =>
                navigate(`/admin/dispatch-history/${dispatch.vehicleId}`)
              }
              className="hover:text-black transition-colors"
            >
              All Dispatch History
            </button>

            <span className="text-gray-300">/</span>

            <button
              onClick={() =>
                navigate(`/admin/dispatch-history/${dispatch.vehicleId}`)
              }
              className="font-medium text-gray-700 hover:text-black transition-colors"
            >
              {dispatch?.dispatchNumber}
            </button>
          </div>

          {/* Main Content */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            {/* Left Section */}
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-400 mb-2">
                Dispatch Details
              </p>

              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight mb-4">
                #{dispatch.dispatchNumber}
              </h1>

              {/* Route */}
              <div className="flex items-center gap-4 text-base font-medium text-gray-700">
                <span className="bg-gray-100 px-3 py-1 rounded-lg">
                  {dispatch.fromLocation}
                </span>

                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100 text-amber-600 text-lg">
                  →
                </div>

                <span className="bg-gray-100 px-3 py-1 rounded-lg">
                  {dispatch.toLocation}
                </span>
              </div>
            </div>

            {/* Right Section */}
            <div className="flex flex-col items-start lg:items-end gap-3">
              <StatusBadge
                status={dispatch.tripStatus}
                completed={dispatch.isCompleted}
              />

              <div className="text-sm text-gray-500">
                <span className="font-medium text-gray-700">
                  {dispatch.approximateDistance} km
                </span>
                <span className="mx-2 text-gray-300">•</span>
                <span>~{dispatch.estimatedTime} min</span>
              </div>

              {dispatch.noOfIncidents > 0 && (
                <span className="text-xs bg-red-100 text-red-600 px-3 py-1 rounded-full font-medium">
                  {dispatch.noOfIncidents} Incident
                  {dispatch.noOfIncidents > 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── KPI Bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-b border-gray-100">
        {/* Income Earned */}
        <div className="p-5 sm:p-6 border-r border-gray-100">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1.5">
            Income Earned
          </p>
          <p className="text-xl sm:text-2xl font-bold text-emerald-600 leading-none">
            {formatCurrency(dispatch.totalIncomeEarned)}
          </p>
        </div>

        {/* Expected Income */}
        <div className="p-5 sm:p-6 border-r border-gray-100">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1.5">
            Expected Income
          </p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-none mb-2">
            {formatCurrency(dispatch.totalExpectedIncome)}
          </p>
          <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all duration-700"
              style={{
                width: `${Math.min(dispatch.incomeAchievementPercentage, 100)}%`,
              }}
            />
          </div>
          <p className="text-[10px] text-gray-400 mt-1">
            {pct(dispatch.incomeAchievementPercentage)} achieved
          </p>
        </div>

        {/* Total Expense */}
        <div className="p-5 sm:p-6 border-r border-gray-100">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1.5">
            Total Expense
          </p>
          <p className="text-xl sm:text-2xl font-bold text-amber-600 leading-none">
            {formatCurrency(dispatch.totalExpense)}
          </p>
        </div>

        {/* Net Banked */}
        <div className="p-5 sm:p-6 border-r border-gray-100">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1.5">
            Net Banked
          </p>
          <p
            className={`text-xl sm:text-2xl font-bold leading-none ${
              dispatch.netBanked >= 0 ? "text-emerald-600" : "text-red-500"
            }`}
          >
            {formatCurrency(dispatch.netBanked)}
          </p>
        </div>

        {/* Passengers */}
        <div className="p-5 sm:p-6">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1.5">
            Passengers
          </p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900 leading-none">
            {dispatch.totalPassengers}
          </p>
          <p className="text-[10px] text-gray-400 mt-1">
            {formatCurrency(dispatch.perPassengerRate)} / pax
          </p>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="px-6 sm:px-10 pb-16">
        {/* Trip Information */}
        <Section title="Trip Information" icon="🗺️">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden">
            <InfoCard label="Driver" value={dispatch.driverName || "—"} />
            <InfoCard
              label="Vehicle Model"
              value={dispatch.vehicleModel || "—"}
            />
            <InfoCard
              label="Distance"
              value={`${dispatch.approximateDistance} km`}
            />
            <InfoCard
              label="Est. Time"
              value={`${dispatch.estimatedTime} min`}
            />
            <InfoCard
              label="Trip Start"
              value={fmtDate(dispatch.tripStartTime)}
            />
            <InfoCard label="Trip End" value={fmtDate(dispatch.tripEndTime)} />
            <InfoCard label="Incidents" value={dispatch.noOfIncidents} />
            <InfoCard
              label="Crew Members"
              value={dispatch.crewMembers || "—"}
            />
          </div>
        </Section>

        {/* Crew */}
        {dispatch.crews?.length > 0 && (
          <Section title="Crew" icon="👥">
            <div className="flex flex-wrap gap-3">
              {dispatch.crews.map((c: any) => (
                <div
                  key={c.id}
                  className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 bg-white hover:bg-gray-50 transition-colors"
                >
                  <div className="w-9 h-9 rounded-full bg-gray-950 text-white flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {c.name?.[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {c.name}
                    </p>
                    <p className="text-[10px] uppercase tracking-wider text-gray-400">
                      {c.crewType}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Income Breakdown */}
        {dispatch.incomes?.length > 0 && (
          <Section title="Income Breakdown" icon="💰">
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="table-cards w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {["Description", "Expected", "Earned", "Variance"].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-gray-400 font-semibold"
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {dispatch.incomes.map((inc: any, idx: number) => {
                    const v = inc.earnedAmount - inc.expectedAmount;
                    return (
                      <tr
                        key={inc.id}
                        className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors ${
                          idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        }`}
                      >
                        <td data-label="Description" className="px-4 py-3 text-gray-700">
                          {inc.description}
                        </td>
                        <td data-label="Expected" className="px-4 py-3 font-semibold text-gray-900">
                          {formatCurrency(inc.expectedAmount)}
                        </td>
                        <td data-label="Earned" className="px-4 py-3 font-semibold text-emerald-600">
                          {formatCurrency(inc.earnedAmount)}
                        </td>
                        <td
                          data-label="Variance"
                          className={`px-4 py-3 font-semibold ${
                            v >= 0 ? "text-emerald-600" : "text-red-500"
                          }`}
                        >
                          {v >= 0 ? "+" : ""}
                          {formatCurrency(v)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Expenses */}
        {dispatch.expenses?.length > 0 && (
          <Section title="Expenses" icon="📋">
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <table className="table-cards w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    {["Description", "Amount"].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-[10px] uppercase tracking-widest text-gray-400 font-semibold"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dispatch.expenses.map((exp: any, idx: number) => (
                    <tr
                      key={exp.id}
                      className={`border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors ${
                        idx % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                      }`}
                    >
                      <td data-label="Description" className="px-4 py-3 text-gray-700">
                        {exp.description}
                      </td>
                      <td data-label="Amount" className="px-4 py-3 font-semibold text-red-500">
                          {formatCurrency(exp.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        )}

        {/* Fuel Expense */}
        {dispatch.fuelExpense && (
          <Section title="Fuel Expense" icon="⛽">
            <div className="border border-gray-200 rounded-xl p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6 bg-white">
              {[
                ["Station", dispatch.fuelExpense.stationName],
                ["Fuel Type", dispatch.fuelExpense.fuelType],
                ["Quantity", `${dispatch.fuelExpense.quantity} L`],
                [
                  "Price / Unit",
                  formatCurrency(dispatch.fuelExpense.pricePerUnit),
                ],
                ["Total Amount", formatCurrency(dispatch.fuelExpense.amount)],
                ["Odometer", `${dispatch.fuelExpense.odometerReading} km`],
                ["Date", fmtDate(dispatch.fuelExpense.fuelDate)],
                ["Dispatch #", dispatch.fuelExpense.dispatchNumber],
              ].map(([label, value]) => (
                <div key={label as string}>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-medium mb-1">
                    {label}
                  </p>
                  <p className="text-sm font-semibold text-gray-900">{value}</p>
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Company */}
        {dispatch.company && (
          <Section title="Company" icon="🏢">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-gray-200 border border-gray-200 rounded-xl overflow-hidden">
              <InfoCard
                label="Company Name"
                value={dispatch.company.fullName}
              />
              <InfoCard
                label="Contact Person"
                value={dispatch.company.contatcPerson}
              />
              <InfoCard label="Email" value={dispatch.company.email} />
              <InfoCard label="Phone" value={dispatch.company.phoneNumber} />
              <InfoCard label="Address" value={dispatch.company.address} />
              <InfoCard
                label="Total Vehicles"
                value={dispatch.company.totalVehicles}
              />
              <InfoCard
                label="Total Partners"
                value={dispatch.company.totalPartners}
              />
              <InfoCard
                label="Total Drivers"
                value={dispatch.company.totalDrivers}
              />
            </div>
          </Section>
        )}
      </div>
    </div>
  );
};

export default DispatchDetails;
